import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { NotificationService } from './shared/notification.service';
import { environment } from '../environments/environment';
import { AuthResult, AutoLoginRequest, RefreshTokenRequest, User } from './models/user';
import { jwtDecode } from 'jwt-decode';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { ClientType, UserType } from './shared/enums';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { CallcentreOperator } from './models/client';

export type AuthSessionSource = 'interactiveLogin' | 'sessionRestore' | 'tokenRefresh' | 'autoLogin';

export interface AuthSessionEvent {
  user: User;
  source: AuthSessionSource;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly callcentreOperatorStorageKey = 'callcentre_operator';
  user: User | undefined = undefined;
  token: string | undefined = undefined;
  callcentreOperator: CallcentreOperator | undefined = undefined;
  loginSubscription = new EventEmitter<AuthSessionEvent>();
  
  constructor(private http: HttpClient, private notification: NotificationService, private router: Router) { }

  public sendOtp(username: string) {
    
    return this.http.post<{detail: string}>(environment.apiUrl + 'v1/user/request-otp/' + username, null);
  }

  public login(username: string, password: string) {
    /*OAuth2 requires the request to be application/x-www-form-urlencoded, not JSON*/
    let body = `username=${username}&password=${password}`;
    let options = {headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')};

    return this.http.post<AuthResult>(environment.apiUrl + 'token', body, options)
      .pipe( 
        tap(res => {
          this.storeDetails(res, 'interactiveLogin');
        })
      );
  }

  public loginWithOtp(username: string, otp: string) {
    /*OAuth2 requires the request to be application/x-www-form-urlencoded, not JSON*/
    let body = `username=${username}&password=${otp}`;
    let options = {headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')};

    return this.http.post<AuthResult>(environment.apiUrl + 'token-otp', body, options)
      .pipe( 
        tap(res => {
          this.storeDetails(res, 'interactiveLogin');
        })
      );
  }

  private storeDetails(authResult: AuthResult, source: AuthSessionSource): void {
    localStorage.setItem("access_token", authResult.access_token);
    if (authResult.refresh_token) {
      localStorage.setItem("refresh_token", authResult.refresh_token);
    } else {
      localStorage.removeItem("refresh_token");
    }
    this.token = authResult.access_token;
    this.user = this.decodeUser(authResult.access_token);
    this.loginSubscription.emit({ user: this.user, source });
  }

  public getStoredLogin(routeParams?: Record<string, string | number>, queryParams?: Record<string, string | number>): boolean | Observable<boolean> {  
    let authToken = localStorage.getItem("access_token");
    if (authToken) {
      // Check if token is expired
      if (this.isTokenExpired(authToken)) {
        // Token is expired, try to refresh
        return this.refreshToken(routeParams, queryParams);
      }
      // Token is valid
      this.token = authToken;
      this.user = this.decodeUser(authToken);
      this.loginSubscription.emit({ user: this.user, source: 'sessionRestore' });
      return true;
    }
    // No stored token — attempt auto-login via route params if present, otherwise false
    return this.refreshToken(routeParams, queryParams);
  }

  hasClaims(claims?: string[], showMessage: boolean = false): boolean | Observable<boolean> {    
    if (claims){
      if (!this.user?.claims) {        
          return false;
      }
      else {
        const val = this.user.claims.some(r => claims.includes(r))
        if (!val && showMessage) this.notification.showWarning("Access denied.");
        return val;
      }
    }
    else {
      return true;
    } 
  }

  hasStoredClaim(claim: string): boolean {
    if (claim){
      if (!this.user?.claims) {        
          return false;
      }
      else {
        const val = this.user.claims.some(c => c === claim)
        return val;
      }
    }
    else {
      return true;
    } 
  }

  public clearLogin() {
    //clear this.user and token
    this.user = undefined;
    this.token = undefined;
    this.router.navigate(['/login']);
  }

  public removeStoredLogin() {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    this.user = undefined;
    this.token = undefined;
  }

  public setCallcentreOperator(operator: CallcentreOperator): void {
    this.callcentreOperator = operator;
    localStorage.setItem(this.callcentreOperatorStorageKey, JSON.stringify(operator));
  }

  public getStoredCallcentreOperator(): CallcentreOperator | undefined {
    const storedOperator = localStorage.getItem(this.callcentreOperatorStorageKey);
    if (!storedOperator) {
      return undefined;
    }

    try {
      const operator = JSON.parse(storedOperator) as CallcentreOperator;
      return Number.isFinite(operator.userId) ? operator : undefined;
    } catch {
      return undefined;
    }
  }

  public clearCallcentreOperatorFromMemory(): void {
    this.callcentreOperator = undefined;
  }

  public isLoggedIn(): boolean {
    return this.user != undefined && this.token != undefined;
  }

  public isCurrentTokenExpired(): boolean {    
    if (!this.token) {
      
      return true;
      
    }
    return this.isTokenExpired(this.token);
  }

  private isTokenExpired(token: string): boolean {
    try {
      const decoded: any = jwtDecode(token);
      if (!decoded.exp) {
        return true;
      }
      const expirationDate = decoded.exp * 1000; // Convert to milliseconds
      const now = new Date().getTime();
      return expirationDate < now;
    } catch (error) {
      return true;
    }
  }

  private decodeUser(token: string): User {
    const user = jwtDecode<User>(token);
    return {
      ...user,
      isWhatsappOnlyNotificationRecipient: user.isWhatsappOnlyNotificationRecipient ?? false
    };
  }

  public refreshToken(routeParams?: Record<string, string | number>, queryParams?: Record<string, string | number>): Observable<boolean> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      const autoLoginRequest = this.buildAutoLoginRequest(routeParams, queryParams);
      if (!autoLoginRequest) {
        this.removeStoredLogin();
        return of(false);
      }

      return this.http.post<AuthResult>(environment.apiUrl + 'autologin', autoLoginRequest)
        .pipe(
          tap(res => {
            this.storeDetails(res, 'autoLogin');
          }),
          map(() => true),
          catchError(error => {
            console.error('Auto login failed:', error);
            this.removeStoredLogin();
            return of(false);
          })
        );
    }

    //let body = `refresh_token=${refreshToken}`;
    //let options = {headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')};
    let req: RefreshTokenRequest = { refresh_token: refreshToken };

    return this.http.post<AuthResult>(environment.apiUrl + 'refresh', req)
      .pipe(
        tap(res => {
          this.storeDetails(res, 'tokenRefresh');
        }),
        map(() => true),
        catchError(error => {
          console.error('Token refresh failed:', error);
          this.removeStoredLogin();
          return of(false);
        })
      );
  }

  private buildAutoLoginRequest(routeParams?: Record<string, string | number>, queryParams?: Record<string, string | number>): AutoLoginRequest | null {
    const context = this.resolveAutoLoginContext(routeParams, queryParams);
    const userId = this.getNumericRouteValue('userId', context);
    const incidentId = this.getNumericRouteValue('incidentId', context);

    if (userId === null || incidentId === null) {
      return null;
    }

    const request = new AutoLoginRequest();
    request.userId = userId;
    request.incidentId = incidentId;
    return request;
  }

  private resolveAutoLoginContext(routeParams?: Record<string, string | number>, queryParams?: Record<string, string | number>) {
    if (routeParams || queryParams) {
      return { routeParams, queryParams };
    }

    const routeSnapshot = this.getDeepestRouteSnapshot(this.router.routerState.snapshot.root);
    return {
      routeParams: routeSnapshot?.params,
      queryParams: routeSnapshot?.queryParams
    };
  }

  private getDeepestRouteSnapshot(snapshot: ActivatedRouteSnapshot | null): ActivatedRouteSnapshot | null {
    let current = snapshot;
    while (current?.firstChild) {
      current = current.firstChild;
    }
    return current;
  }

  private getNumericRouteValue(key: 'userId' | 'incidentId', context: { routeParams?: Record<string, string | number>, queryParams?: Record<string, string | number> }): number | null {
    const routeValue = context.routeParams?.[key];
    const queryValue = context.queryParams?.[key];
    const rawValue = routeValue ?? queryValue;

    if (rawValue === undefined || rawValue === null || rawValue === '') {
      return null;
    }

    const numericValue = Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  
  public get isDriver(): boolean {
    return this.isLoggedIn() && [UserType.truckdriver, UserType.technician].includes(this.user?.userType ?? 0);
  }

  public get isTruckDriver(): boolean {
    return this.isLoggedIn() && [UserType.truckdriver].includes(this.user?.userType ?? 0);
  }

  public get isBreakdownTechnician(): boolean {
    return this.isLoggedIn() && [UserType.technician].includes(this.user?.userType ?? 0);
  }

  public get isOperator(): boolean {
    return this.isLoggedIn() && [UserType.operator].includes(this.user?.userType ?? 0);
  }

  public get isTechnician(): boolean {
    return this.isLoggedIn() && [UserType.technician].includes(this.user?.userType ?? 0);
  }

  public get isClientFleet(): boolean {
    return (this.isLoggedIn() && this.user?.clientTypes.includes(ClientType.fleet)) ?? false;
  }

  public get isClientCallCentre(): boolean {
    return (this.isLoggedIn() && this.user?.clientTypes.includes(ClientType.call_centre)) ?? false;
  }

  public get isClientServiceProvider(): boolean {
    return (this.isLoggedIn() && this.user?.clientTypes.includes(ClientType.service_provider)) ?? false;
  }



}
